import { Link } from "react-router-dom";
import { Clock, ArrowRight, CheckCircle, GraduationCap, UserCheck } from "lucide-react";

const courses = [
  {
    title: "Bachelors of Hotel Management and Catering Technology",
    duration: "3 Years",
    type: "Undergraduate",
    eligibility: "Pass in Intermediate Exam (10+2) or Equivalent",
    degree: "Affiliated to Osmania University",
    desc: "A comprehensive degree program covering all aspects of hotel operations, food production, food & beverage service, front office management, and catering technology.",
  },
  {
    title: "B.Sc in Hotel Management",
    duration: "3 Years",
    type: "Undergraduate",
    eligibility: "Pass in Intermediate Exam (10+2) or Equivalent",
    degree: null,
    desc: "A well-rounded degree covering hotel operations, hospitality management, food production, and industry-ready professional skills.",
  },
  {
    title: "Diploma in Culinary Arts",
    duration: "1 Year",
    type: "Diploma",
    eligibility: "Pass in 10th / Intermediate",
    degree: null,
    desc: "Master the art and science of cooking with hands-on training in international cuisines, baking, and professional kitchen management.",
  },
  {
    title: "International Diploma in Hotel Management",
    duration: "1 Year 6 Months",
    type: "Diploma",
    eligibility: "Pass in 10th / Intermediate",
    degree: null,
    desc: "An internationally recognized diploma providing comprehensive training in hotel operations, guest services, and hospitality management.",
  },
  {
    title: "Diploma in Bartending",
    duration: "1 Year 6 Months",
    type: "Diploma",
    eligibility: "Pass in 10th / Intermediate",
    degree: null,
    desc: "Specialize in the art of bartending with hands-on training in cocktail making, beverage knowledge, and bar operations management.",
  },
  {
    title: "Certificate in Bar Management / Mixology",
    duration: "6 Months",
    type: "Certificate",
    eligibility: "10th Pass",
    degree: null,
    desc: "A focused short-term program in bar management, mixology techniques, cocktail creation, and beverage service excellence.",
  },
  {
    title: "Integrated Program",
    duration: "5 Years",
    type: "Undergraduate",
    eligibility: "Pass in 10th",
    degree: "Affiliated to Osmania University",
    desc: "A comprehensive 5-year integrated program combining foundational education with advanced hotel management studies for a seamless career path.",
  },
];

const admissionSteps = [
  { step: 1, title: "Online Application", desc: "Fill out the online application form with your personal and academic details." },
  { step: 2, title: "Document Submission", desc: "Upload your 10th and 12th mark sheets, identity proof, and passport-sized photos." },
  { step: 3, title: "Entrance Assessment", desc: "Appear for the Vainavi aptitude test covering general knowledge and English proficiency." },
  { step: 4, title: "Personal Interview", desc: "Attend a personal interview with our admissions committee at the Hyderabad campus." },
  { step: 5, title: "Offer Letter", desc: "Successful candidates receive an offer letter with course details and fee structure." },
  { step: 6, title: "Enrollment & Orientation", desc: "Complete the enrollment formalities and attend the orientation program." },
];

export default function Courses() {
  return (
    <div data-testid="courses-page">
      {/* Hero Banner */}
      <section className="relative pt-36 pb-20 bg-navy">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <p className="font-body text-sm uppercase tracking-[0.3em] text-white/70 mb-3">Programs</p>
          <h1 data-testid="courses-hero-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Courses & Admissions
          </h1>
          <p className="font-body text-lg text-white/80 max-w-2xl">
            Discover our range of hospitality programs designed to launch your career in the global hotel industry.
          </p>
        </div>
      </section>

      {/* Programs */}
      <section data-testid="programs-section" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="font-body text-sm uppercase tracking-[0.3em] text-crimson mb-3">Our Programs</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy tracking-tight">
              Programs Offered
            </h2>
            <div className="w-16 h-1 bg-crimson mx-auto mt-4" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course, i) => (
              <div
                key={course.title}
                data-testid={`course-card-${i}`}
                className="bg-white border border-slate-100 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden group"
              >
                <div className="bg-navy p-6">
                  <span className="font-body text-xs uppercase tracking-wider text-crimson-light bg-white/10 px-3 py-1">
                    {course.type}
                  </span>
                  <h3 className="font-heading text-xl font-semibold text-white mt-3">{course.title}</h3>
                  <div className="flex items-center gap-2 mt-2 text-white/70">
                    <Clock className="w-4 h-4" />
                    <span className="font-body text-sm">{course.duration}</span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="font-body text-sm text-slate-600 leading-relaxed mb-4">{course.desc}</p>
                  <div className="space-y-2.5 mb-6">
                    <div className="flex items-start gap-2">
                      <UserCheck className="w-4 h-4 text-crimson flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-body text-xs uppercase tracking-wider text-slate-400">Eligibility</span>
                        <p className="font-body text-sm text-slate-700">{course.eligibility}</p>
                      </div>
                    </div>
                    {course.degree && (
                      <div className="flex items-start gap-2">
                        <GraduationCap className="w-4 h-4 text-crimson flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-body text-xs uppercase tracking-wider text-slate-400">Degree</span>
                          <p className="font-body text-sm font-medium text-navy">{course.degree}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <Link
                    to="/contact"
                    data-testid={`course-inquire-btn-${i}`}
                    className="inline-flex items-center gap-2 bg-crimson text-white px-6 py-2.5 font-body text-xs font-semibold uppercase tracking-wider hover:bg-crimson-hover transition-all duration-300"
                  >
                    Inquire Now <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Admissions Process */}
      <section data-testid="admissions-section" className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="font-body text-sm uppercase tracking-[0.3em] text-crimson mb-3">How to Apply</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy tracking-tight">
              Admissions Process
            </h2>
            <div className="w-16 h-1 bg-crimson mx-auto mt-4" />
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-0">
              {admissionSteps.map((s, i) => (
                <div
                  key={s.step}
                  data-testid={`admission-step-${i}`}
                  className="flex gap-6 group"
                >
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 flex items-center justify-center bg-crimson text-white font-heading font-bold text-lg flex-shrink-0">
                      {s.step}
                    </div>
                    {i < admissionSteps.length - 1 && (
                      <div className="w-0.5 h-full bg-slate-200 my-1" />
                    )}
                  </div>
                  <div className="pb-10">
                    <h3 className="font-heading text-xl font-semibold text-navy mb-2">{s.title}</h3>
                    <p className="font-body text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-crimson py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Start Your Application</h2>
          <p className="font-body text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Admissions for the upcoming session are now open. Secure your seat at Hyderabad&apos;s premier hotel management institution.
          </p>
          <Link
            to="/contact"
            data-testid="courses-cta-btn"
            className="inline-flex items-center gap-2 bg-navy text-white px-10 py-4 font-body text-sm font-semibold uppercase tracking-wider hover:bg-navy-hover transition-all duration-300"
          >
            Apply Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
